import React, { FC, useEffect, useState } from "react";
import { EndPoints } from "../../../../utils/apiEndpoints";
import { useNavigate, useParams } from "react-router-dom";
import { AddTicketItems } from "../Components/AddTicketItems";
import { FieldErrors } from "../../../../domain/helpers/Errors";
import { validateSchema } from "../../../../common/ValidateSchema";
import { AddTicketColumns } from "../Components/AddTicketColumns";
import { darkModeBackgroudInputs } from "../../../../utils/constants";
import { useLanguageStore } from "../../../../store/useLanguageStore";
import { useDarkModeStore } from "../../../../store/useDarkModeStore";
import { TicketInterface } from "../../../../models/TicketModule/Ticket";
import { TicketFinancialType } from "../../../../enums/ticketFinancialType";
import { ticketSchema } from "../../../../domain/ValidationSchemas/Ticket/ticket";
import { assetActionSchema } from "../../../../domain/ValidationSchemas/Asset/assetAction";
import { AssetActionInterface } from "../../../../models/AssetModel/AssetAction/AssetAction";
import { AddTicketModal, initialAssetAction, initialTicket, saveBtnStyle, sheetCss, showColumnModal } from "../AddTicket";
import dayjs from "dayjs";
import Sheet from "@mui/joy/Sheet";
import Button from "@mui/joy/Button";
import Timeline from "../Components/Timeline";
import IconButton from "@mui/joy/IconButton";
import Skeleton from 'react-loading-skeleton'
import CssBaseline from "@mui/joy/CssBaseline";
import ModalForm from "../../../../common/ModalForm";
import LoadingIcon from "../../../../common/LoadingIcon";
import ToastMessage from "../../../../common/ToastMessage";
import FeedbackModal from "../TicketModal/FeedbackModal";
import useScreenWidth from "../../../../store/useScreenWidth";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import CreateRoundedIcon from '@mui/icons-material/CreateRounded';
import CustomizedInputsStyled from "../Components/CustomizedInputStyle";
import useAddTicket from "../../../../store/ReactQueries/tickets/useAddTickets";
import useTicketById from "../../../../store/ReactQueries/tickets/useTicketById";
import useUpdateTicket from "../../../../store/ReactQueries/tickets/useUpdateTicket";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import useUpdateRecTicket from "../../../../store/ReactQueries/recurringTickets/useUpdateRecTicket";
import useAddAssetActions from "../../../../store/ReactQueries/assets/assetActions/useAddAssetAction";

interface AddSubTicketProps {
    view?: boolean;
    isRecurringTicket?: boolean;
}
const { RECURRINGTICKETS, TICKETS, SUBTICKET } = EndPoints;

export const AddSubTicket: FC<AddSubTicketProps> = ({ view = false, isRecurringTicket = false }) => {

    const navigate = useNavigate();
    const addNewTicket = useAddTicket();
    const isSmallScreen = useScreenWidth();

    const { darkMode } = useDarkModeStore();
    const { language } = useLanguageStore();

    const { id: ticketId, readFeedback, subticketId } = useParams<{ id: string; readFeedback: string; subticketId: string }>();
    const { data: ticketById, error: ticketByIdError, isLoading } = useTicketById(subticketId || "");

    const isReadFeedback = readFeedback === "true";
    const navigateEndpoint = isRecurringTicket ? RECURRINGTICKETS : TICKETS;

    const updateTicket = useUpdateTicket(1, 20);
    const addAssetAction = useAddAssetActions(0, 0);
    const updateRecurringTicket = useUpdateRecTicket(1, 20);

    const [errors, setErrors] = useState<FieldErrors>({});
    const [loading, setLoading] = useState<boolean>(false);
    const [form, setForm] = useState<TicketInterface>(initialTicket);
    const [openFeedback, setOpenFeedback] = useState<boolean>(false);
    const [disableSaveBtn, setDisableBtnSave] = useState<boolean>(false);
    const [errorsAssetAction, setErrorsAssetAction] = useState<FieldErrors>({});
    const [modal, setModal] = useState<AddTicketModal>({ add: false, edit: false, view: false });
    const [formAssetAction, setFormAssetAction] = useState<AssetActionInterface>(initialAssetAction);

    const handleSuccess = (
        resolve: (value: void | PromiseLike<void>) => void
    ) => {
        ToastMessage.Success();
        navigate(`${navigateEndpoint}/edit/${ticketId}`);
        resolve();
    };

    const handleError = (error: unknown, reject: (reason?: any) => void) => {
        if (error instanceof Error) ToastMessage.Failed(error.message);
        else ToastMessage.Failed();
        reject(error);
    };

    const handleSave = async () => {
        const isValid = validateSchema(ticketSchema, form);
        setErrors(isValid.errors);
        if (!isValid.result) return;

        if (form.status?.isCompleted && form.assetId) {
            setModal({ ...modal, add: true, edit: false, view: false });
        } else saveTicket();
    };

    const saveTicket = async () => {
        setDisableBtnSave(true);
        try {
            await new Promise<void>((resolve, reject) => {
                let upForm: TicketInterface = {
                    id: form.id,
                    title: form.title,
                    images: form.images,
                    dueDate: form.dueDate,
                    statusId: form.statusId,
                    categoryId: form.categoryId,
                    locationId: form.locationId,
                    priorityId: form.priorityId,
                    customerId: form.customerId,
                    formImages: form.formImages,
                    description: form.description,
                    contractorId: form.contractorId,
                    departmentId: form.departmentId,
                    interventionTypeId: form.interventionTypeId,
                    companyId: form.companyId,
                    isRecurring: form.isRecurring ?? false,
                    masterTicketId: ticketId || "",
                };
                if (form.isRecurring) {
                    upForm.recurringPeriod = form.recurringPeriod;
                    upForm.recurringStartTime = form.recurringStartTime;
                }
                if (form.assignedToId) {
                    upForm.assignedToId = form.assignedToId;
                }
                if (form.assetId) {
                    upForm.assetId = form.assetId;
                }
                if (form.ticketFinancialType === TicketFinancialType.Billable) {
                    upForm.ticketFinancialType = TicketFinancialType.Billable;
                    upForm.financialAssignedToId = form.financialAssignedToId;
                }

                if (upForm.id === "") {
                    const { id, ...formData } = upForm;
                    addNewTicket.mutate(formData, {
                        onSuccess: () => handleSuccess(resolve),
                        onError: (error) => handleError(error, reject),
                    });
                } else {
                    let updateForm = { ...upForm };
                    if (updateForm.formImages === null) {
                        delete updateForm.formImages;
                    }
                    if (isRecurringTicket) {
                        updateRecurringTicket.mutate(upForm, {
                            onSuccess: () => handleSuccess(resolve),
                            onError: (error: unknown) => handleError(error, reject),
                        });
                    } else {
                        updateTicket.mutate(upForm, {
                            onSuccess: () => handleSuccess(resolve),
                            onError: (error: unknown) => handleError(error, reject),
                        });
                    }
                }
            });
        } catch (ex: unknown) {
            if (ex instanceof Error) {
                console.log(ex.message);
            } else {
                console.log("An unknown error occurred");
            }
        }
        setTimeout(() => {
            setDisableBtnSave(false);
        }, 300);
    };

    const handleFeedback = () => setOpenFeedback(true);
    const handleCloseFeedback = () => setOpenFeedback(false);

    const handleSaveAssetAction = async (assetid: string | undefined | null) => {
        let formA = { ...formAssetAction };
        formA.assetId = assetid || "";

        const isValid = validateSchema(assetActionSchema, formA);

        setErrorsAssetAction(isValid.errors);
        if (!isValid.result) return;

        saveTicket();

        const { id, title, assetActionTypeId, amount, date, description, assetId } = formA;
        const upForm: AssetActionInterface = {
            id,
            date,
            title,
            amount,
            assetId,
            description,
            assetActionTypeId,
        };

        if (upForm.id === "") {
            const { id, ...formData } = upForm;
            addAssetAction.mutate(formData, {
                onError: (error) => ToastMessage.Failed(error.message),
            });
        }
        setModal({ ...modal, add: false, edit: false, view: false });
    };

    useEffect(() => {
        if (isReadFeedback) setOpenFeedback(true);
    }, [isReadFeedback]);

    useEffect(() => {
        if (ticketByIdError) {
            ToastMessage.Failed(`Error: ${ticketByIdError.message}`);
            navigate(-1);
        }
    }, [ticketByIdError]);

    useEffect(() => {
        if (subticketId && ticketById) setForm(ticketById as TicketInterface);
    }, [subticketId, ticketById]);

    useEffect(() => {
        setLoading(true);

        const timer = setTimeout(() => {
            setLoading(false);
        }, 1000);

        return () => clearTimeout(timer);
    }, []);

    const HrLine = () => <hr className={darkMode ? "text-white opacity-100" : ""} />;

    const handleNavigate = () => {
        if (form.masterTicketId && form.id) {
            navigate(`${TICKETS}/edit/${form.masterTicketId}${SUBTICKET}/${form.id}`)
        }
    }

    return <div className="w-100 d-flex justify-content-center ">
        <CssBaseline />
        {isLoading
            ? <LoadingIcon width={50} />
            : <div className="d-flex flex-column align-items-center gap-2 w-100">
                <Sheet
                    sx={{
                        ...sheetCss,
                        background: darkMode ? "var(--dark-mode-bg)" : "white",
                    }}
                >
                    <div
                        className="mb-0 d-flex align-items-center"
                        style={{
                            color: darkMode ? "white" : "rgba(0, 0, 0, 0.6)",
                            fontSize: "20px",
                        }}
                    >
                        <ConfirmationNumberOutlinedIcon className="me-2" />
                        {language["SubTicket"]}
                        {
                            form.ticketNo
                                ? loading
                                    ? <Skeleton style={{ marginLeft: 10, marginBottom: 5, width: 145, height: 24 }} />
                                    : ` ${form.ticketNo}`
                                : ` ${language['Add']}`
                        }
                        {
                            view && <IconButton
                                className={`ms-2 ${darkMode && 'text-white'}`}
                                title={language['EditTicket']}
                                onClick={handleNavigate}
                            >
                                <CreateRoundedIcon sx={{ width: 18, height: 18 }} />
                            </IconButton>
                        }
                        {
                            form.id !== ""
                                ? loading
                                    ? <div className="ms-auto"><Skeleton style={{ width: 36, height: 36 }} /></div>
                                    : <IconButton
                                        title={language["Feedback"]}
                                        className="ms-auto"
                                        style={darkMode ? { color: 'white' } : {}}
                                        onClick={handleFeedback}
                                    >
                                        <ChatOutlinedIcon />
                                    </IconButton>
                                : null
                        }
                    </div>

                    <hr style={darkMode ? { color: 'white', opacity: 1 } : {}} />

                    <div className="p-2">
                        {AddTicketItems?.map(({ id, data, section }) => (
                            <div key={id + 'AddTicketItems'}>
                                <div
                                    className=" mt-3 mb-3"
                                    style={{
                                        borderBottom: `2px solid ${darkMode ? "white" : "#D8DDE2"}`,
                                        color: darkMode ? "white" : "rgba(0,0,0,.8)",
                                    }}
                                >
                                    {language[section]}
                                </div>
                                <form
                                    className={`${isSmallScreen ? "d-flex flex-column" : "module-form-grid-ticket"
                                        } gap-2`}
                                >
                                    {
                                        data?.map(({ id, name, label, type }, index) => (
                                            <CustomizedInputsStyled
                                                key={id + index + '-CustomizedInputsStyled'}
                                                name={name}
                                                type={type}
                                                form={form}
                                                view={view}
                                                label={label}
                                                errors={errors}
                                                loading={loading}
                                                isSubTicket={true}
                                                setForm={setForm}
                                            />
                                        ))
                                    }
                                </form>
                            </div>
                        ))}
                    </div>
                    {ticketById
                        && ticketById.ticketTimelines
                        && ticketById.ticketTimelines.length > 0
                        && <React.Fragment>
                            <Timeline ticketById={ticketById} />
                            <HrLine />
                        </React.Fragment>
                    }
                    <div className="d-flex flex-row justify-content-end gap-2">
                        <Button
                            sx={{
                                fontSize: "16px",
                                background: darkMode ? darkModeBackgroudInputs : "white",
                                border: `1px solid var(--btn-color)`,
                                color: darkMode ? 'white' : "var(--btn-color)",
                                fontWeight: 700,
                                "&:hover": {
                                    background: darkMode ? darkModeBackgroudInputs : "white",
                                    opacity: .7,
                                },
                            }}
                            onClick={() => navigate(`${navigateEndpoint}/edit/${ticketId}`)}
                        >
                            {language["Cancel"]}
                        </Button>
                        {
                            !view
                            && <Button
                                sx={saveBtnStyle}
                                onClick={handleSave}
                            >
                                {
                                    disableSaveBtn
                                        ? <LoadingIcon width={22} />
                                        : language["Save"]
                                }
                            </Button>
                        }
                    </div>
                    {
                        ticketById
                        && <div
                            className={`position-absolute d-flex flex-row gap-1 ${darkMode ? 'text-white' : 'text-muted'}`}
                            style={{ bottom: 10, left: 10, fontSize: 11 }}
                        >
                            <span>{language['CreatedAt']}: {ticketById.createdDate ? dayjs(ticketById.createdDate).format('DD/MM/YYYY HH:mm') : ''}</span>

                            {ticketById.author && <span>{language['by']} {`${ticketById.author.firstname ?? ''} ${ticketById.author.lastname ?? ''}`}</span>}
                        </div>
                    }

                </Sheet>
                {
                    <ModalForm
                        columns={AddTicketColumns
                            .filter((col) =>
                                showColumnModal.some((item) => item === col.field)
                            )
                            .sort(
                                (a, b) =>
                                    showColumnModal.indexOf(a.field) -
                                    showColumnModal.indexOf(b.field)
                            )}
                        open={modal}
                        form={formAssetAction}
                        errors={errorsAssetAction}
                        setOpen={setModal}
                        setForm={setFormAssetAction}
                        handleSave={() => handleSaveAssetAction(form?.assetId)}
                    />
                }
            </div>
        }
        {
            openFeedback
            && <FeedbackModal
                ticketId={form.id || ""}
                open={openFeedback}
                handleClose={handleCloseFeedback}
            />
        }
    </div>
};
export default AddSubTicket;
